import type { AskHelixNumericBindingDto } from '../../ask-helix';
import type { AskDeclaredExhibitBody } from './declared-exhibit';
import { compareText, coordinateKey, memberLabel } from './declared-measure-table';
import type { AskMeasureColumnSource } from './declared-measure-table';
import { agreedFigurePeriodBasis, type FigurePeriodBasis } from '../../figure-period-basis';

type Binding = AskHelixNumericBindingDto;
type Period = Readonly<{ key: string; label: string; start: string; end: string }>;
type Member = Readonly<{ key: string; label: string }>;

/** One governed series with a dimension axis: existing table, existing binding cells. */
export function declarePeriodTable(
  outputs: readonly AskMeasureColumnSource[],
): AskDeclaredExhibitBody | null {
  if (outputs.length !== 1 || outputs[0].operation !== 'series') return null;
  const [output] = outputs;
  if (!oneDimension(output.bindings)) return null;
  const periods = declaredPeriods(output.bindings);
  const members = declaredMembers(output.bindings);
  const cells = keyedCells(output.bindings);
  if (periods === null || members === null || cells === null) return null;
  const columns = periods.map(period => ({
    outputId: JSON.stringify([output.outputId, period.key]), label: period.label,
    ...columnBasis(output.bindings.filter(binding => periodKey(binding) === period.key)),
  }));
  const forecast = columns.find(column => column.periodBasis === 'forecast');
  return {
    kind: 'decision_table', subject: null,
    columns,
    ...(forecast ? { forecastStartColumnId: forecast.outputId } : {}),
    rows: members.map((member) => ({
      label: member.label,
      cells: periods.map((period) => cells.get(cellKey(member.key, period.key)) ?? null),
    })),
  };
}

function columnBasis(bindings: readonly Binding[]): { periodBasis?: FigurePeriodBasis } {
  const periodBasis = agreedFigurePeriodBasis(bindings.map(binding => binding.timeframe.periodBasis));
  return periodBasis ? { periodBasis } : {};
}

function oneDimension(bindings: readonly Binding[]): boolean {
  if (bindings.length === 0) return false;
  const dimensions = new Set<string>();
  for (const binding of bindings) {
    const coordinates = Object.entries(binding.coordinates ?? {});
    if (binding.claimRole !== 'row' || coordinates.length !== 1) return false;
    const [dimension, member] = coordinates[0];
    if (dimension.length === 0 || member.length === 0) return false;
    dimensions.add(dimension);
  }
  return dimensions.size === 1 && new Set(bindings.map((binding) => binding.unit)).size === 1;
}

/** Labels describe columns; the full typed timeframe identifies them. */
function periodKey(binding: Binding): string {
  const { kind, start, end, asOf } = binding.timeframe;
  return JSON.stringify([kind, start, end, asOf ?? null]);
}

function declaredPeriods(bindings: readonly Binding[]): readonly Period[] | null {
  const periods = new Map<string, Period>();
  for (const binding of bindings) {
    const { start, end, label } = binding.timeframe;
    if (!start || !end || !label || start > end) return null;
    const key = periodKey(binding);
    const previous = periods.get(key);
    if (previous !== undefined && previous.label !== label) return null;
    periods.set(key, { key, label, start, end });
  }
  const ordered = [...periods.values()].sort((a, b) => compareText(a.start, b.start));
  if (new Set(ordered.map((period) => period.label)).size !== ordered.length) return null;
  if (ordered.some((period, index) => index > 0 && period.start <= ordered[index - 1].end)) return null;
  return ordered;
}

function declaredMembers(bindings: readonly Binding[]): readonly Member[] | null {
  const members = new Map<string, Member>();
  for (const binding of bindings) {
    const key = coordinateKey(binding);
    const label = memberLabel(binding);
    if (!label || (members.has(key) && members.get(key)?.label !== label)) return null;
    members.set(key, { key, label });
  }
  const rows = [...members.values()];
  if (new Set(rows.map((row) => row.label)).size !== rows.length) return null;
  return rows.sort((a, b) => compareText(a.label, b.label));
}

function cellKey(member: string, period: string): string {
  return JSON.stringify([member, period]);
}

/** Duplicate coordinates or placeholders are ambiguous, never first/last wins. */
function keyedCells(bindings: readonly Binding[]): ReadonlyMap<string, string> | null {
  if (new Set(bindings.map((binding) => binding.placeholder)).size !== bindings.length) return null;
  const cells = new Map(bindings.map((binding) => [
    cellKey(coordinateKey(binding), periodKey(binding)), binding.placeholder,
  ]));
  return cells.size === bindings.length ? cells : null;
}
